import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Typography } from '@mui/material'
import flatten from 'lodash/flatten'
import { useEffect, useMemo, useState } from 'react'

import {
  AddItemToCartMutation,
  AddItemToCartMutationVariables,
  ArtistStoreItemDetailForGuestQuery,
  ArtistStoreItemDetailForGuestQueryVariables,
  ArtistStoreItemDetailQuery,
  ArtistStoreItemDetailQueryVariables,
  ArtistStoreItemDetailWithSkuQuery,
  ArtistStoreItemDetailWithSkuQueryVariables,
  ArtistSubscriptionPlanFragmentDoc,
  CreatePaymentMethod,
  CreatePaymentMethodMutation,
  CreatePaymentMethodMutationVariables,
  HasItemUserItemDetail,
  HasItemUserItemDetailQuery,
  HasItemUserItemDetailQueryVariables,
  MembershipInterval,
  PaymentCreateDetailFragmentDoc,
  PurchasablePrepaidPointConnectionFragmentDoc,
  PurchaseItemByPointMutation,
  PurchaseItemByPointMutationVariables,
  PurchaseItemImmediatelyMutation,
  PurchaseItemImmediatelyMutationVariables,
  SendVerificationSms,
  SendVerificationSmsMutation,
  SendVerificationSmsMutationVariables,
  StoreItemDetailFragmentDoc,
  StoreItemDetailGuestFragmentDoc,
  StoreItemDetailGuestSkuFragmentDoc,
  UpdateUserDeliveryInfo,
  UpdateUserDeliveryInfoMutation,
  UpdateUserDeliveryInfoMutationVariables,
  UserDeliveryInfoFragment,
  UserDeliveryInfoFragmentDoc,
  VerifySmsCode,
  VerifySmsCodeMutation,
  VerifySmsCodeMutationVariables,
  ViewStoreItemMutation,
  ViewStoreItemMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { usePointPurchase } from '@/components/pages/PrepaidPointCharge/viewModel'
import { config, constants } from '@/config'
import { useCurrentUser, useMessage, useRouter, useTranslation } from '@/core'
import { ConversionCategories } from '@/core/analytics/ConversionKeys'
import { makeViewModel } from '@/core/architecture'
import { getAccessToken, useAuth } from '@/core/auth'
import { App } from '@/core/native'
import { Share } from '@/core/native'
import { getPlatform } from '@/core/native/app'
import { setAnonymousUser } from '@/core/native/storage'
import { useMetamask } from '@/core/web3/metamask'
import { useWeb3authWalletConnect } from '@/core/web3/web3auth/hooks'
import { generateTransactionId, useAnalytics } from '@/lib/analytics'
import { sleep } from '@/lib/sleep'
import { confirmCardSetupWithStripe, createTokenWithCard } from '@/lib/stripe'
import { useCreateShortLink } from '@/lib/url/shortLink'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { signInAnonymously } = useAuth()
  const { analytics } = useAnalytics()
  const { t } = useTranslation('storeItemDetail')
  const { router, generatePath, routerPages } = useRouter<
    {
      itemId: string
      artistId: string
    },
    { canGoBack: boolean } | undefined
  >()

  const [openSMSVerificationModal, setOpenSMSVerificationModal] =
    useState(false)
  const artistId = router.query.artistId ?? config.artistId
  const itemId = router.query.itemId
  const [signingAnonymously, setSigningAnonymously] = useState(false)
  const [skuWillResaleAvailable, setSkuWillResaleAvailable] =
    useState<boolean>()
  const [skuId, setSkuId] = useState<string>()
  const [skuButtonActive, setSkuButtonActive] = useState<boolean>()

  const { userAuthorizeWalletBySignatureMessage } = useMetamask()
  const platform = getPlatform()
  const {
    purchaseByIosAndAndroid,
    purchaseByWeb,
    chargeLoading,
    completeNativePurchase,
    setCompleteNativePurchase,
  } = usePointPurchase(user?.uid, platform, 'artistStoreItemDetail')

  const { data, loading, refetch } = useQuery<
    ArtistStoreItemDetailQuery,
    ArtistStoreItemDetailQueryVariables
  >(QUERY, {
    variables: {
      id: itemId,
      artistId,
      itemId,
      purchasablePrepaidPointPageInput: { limit: 30 },
      platform,
    },

    returnPartialData: true,
    skip: !user?.isSigned,
    pollInterval: completeNativePurchase ? 2000 : 0,
  })
  const [
    getArtistStoreItemDetailWithSku,
    { data: dataSku, loading: loadingItemWithSku },
  ] = useLazyQuery<
    ArtistStoreItemDetailWithSkuQuery,
    ArtistStoreItemDetailWithSkuQueryVariables
  >(QUERY_ITEM_WITH_SKU)

  const { data: dataForGuest, loading: loadingForGuest } = useQuery<
    ArtistStoreItemDetailForGuestQuery,
    ArtistStoreItemDetailForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: { id: itemId, artistId },
    returnPartialData: true,
    skip: user?.isSigned,
  })

  const [addToCart, { loading: addingToCart }] = useMutation<
    AddItemToCartMutation,
    AddItemToCartMutationVariables
  >(ADD_ITEM_TO_CART)

  const { web3authWalletLoading } = useWeb3authWalletConnect({
    loading: loading || loadingForGuest,
    walletAddress: data?.user?.cryptoWallet?.address,
    isArtist: false,
    refetch,
  })

  const [viewStoreItem] = useMutation<
    ViewStoreItemMutation,
    ViewStoreItemMutationVariables
  >(VIEW_STORE_ITEM)

  const hasItem = !!useQuery<
    HasItemUserItemDetailQuery,
    HasItemUserItemDetailQueryVariables
  >(HasItemUserItemDetail, {
    variables: { itemId, userId: user?.uid },
  }).data?.portfolio

  const [purchaseItemByPoint, { loading: itemPurchasing }] = useMutation<
    PurchaseItemByPointMutation,
    PurchaseItemByPointMutationVariables
  >(PURCHASE_ITEM_BY_POINT)

  const [purchaseItemImmediately] = useMutation<
    PurchaseItemImmediatelyMutation,
    PurchaseItemImmediatelyMutationVariables
  >(PURCHASE_ITEM_IMMEDIATELY)

  const [sendVerificationSMS, { loading: sendingVerificationLoading }] =
    useMutation<
      SendVerificationSmsMutation,
      SendVerificationSmsMutationVariables
    >(SendVerificationSms)

  const [updateUserDeliveryInfo] = useMutation<
    UpdateUserDeliveryInfoMutation,
    UpdateUserDeliveryInfoMutationVariables
  >(UpdateUserDeliveryInfo)

  const [verifySMSCode, { loading: verifyingSMSCode }] = useMutation<
    VerifySmsCodeMutation,
    VerifySmsCodeMutationVariables
  >(VerifySmsCode)

  const [createPaymentMethod] = useMutation<
    CreatePaymentMethodMutation,
    CreatePaymentMethodMutationVariables
  >(CreatePaymentMethod)

  // 決済後、prepaidPointBalanceが更新されたらpollingを停止する
  useEffect(() => {
    setCompleteNativePurchase(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.prepaidPointBalance])

  // ストア閲覧履歴ミューテーションを送信する
  useEffect(() => {
    if (!data?.item?.id) return

    viewStoreItem({
      variables: {
        inputs: {
          itemId: data?.item?.id,
        },
      },
    })
  }, [data?.item?.id])

  const { showError, showMessage, handleClose } = useMessage()

  /**
   * 購入前に電話番号(個人情報)登録を必要とするか
   * チケットアイテムの購入において必須となる
   *  */
  const requirePersonalInfo = !!(
    user &&
    data?.item?.requirePersonalInformation &&
    !data?.user?.isPhoneNumberVerified
  )

  const item = useMemo(() => {
    if (dataSku?.item?.id === router.query.itemId) {
      return dataSku?.item ?? data?.item ?? dataForGuest?.item ?? null
    }
    return data?.item ?? dataForGuest?.item ?? null
  }, [data?.item, dataForGuest?.item, dataSku?.item, router.query.itemId])

  const userActiveResaleItemRaffle = useMemo(() => {
    if (dataSku?.item?.id === router.query.itemId) {
      return dataSku?.userActiveResaleItemRaffle ?? null
    }
    return data?.userActiveResaleItemRaffle ?? null
  }, [
    data?.userActiveResaleItemRaffle,
    dataSku?.item?.id,
    dataSku?.userActiveResaleItemRaffle,
    router.query.itemId,
  ])

  // アーティストプランのリストを作成
  const plans = useMemo(() => {
    const artist = data?.artist ?? dataForGuest?.artist
    if (artist?.subscription?.products?.length) {
      return flatten(
        artist?.subscription?.products.map(product => {
          const monthPlan = product.plans.find(
            plan => plan.interval === MembershipInterval.Month
          )
          if (monthPlan) {
            return [{ ...monthPlan, title: product.title }]
          }
          return product.plans.map(plan => ({ ...plan, title: product.title }))
        })
      )
    }
    if (artist?.subscription?.plans?.length) {
      return artist.subscription.plans
    }
    return []
  }, [
    data?.artist?.subscription?.plans,
    data?.artist?.subscription?.products,
    dataForGuest?.artist?.subscription?.plans,
    dataForGuest?.artist?.subscription?.products,
  ])

  // アイテムを購入可能なプラン名のリスト
  const purchasablePlanNames = useMemo(() => {
    const item = data?.item ?? dataForGuest?.item
    if (item?.isPremium && !item?.purchesablePlanIds) {
      // isPremuimがtrueかつpurchesablePlanIdsが空の場合は、全てのプランを表示
      return plans.map(plan => plan.title)
    }
    return plans
      .filter(plan => item?.purchesablePlanIds?.includes(plan.planId))
      .map(plan => plan.title)
  }, [data?.item?.purchesablePlanIds, plans, dataForGuest?.item])

  const getShortLinkUrl = useCreateShortLink({
    path: generatePath(routerPages.ArtistStoreItemDetail, router.query),
  })

  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  //リセール前か判定するwillResaleAvailableがsku単位で取得できないため、フロントでsku単位ごとのwillResaleAvailableを作成
  const IsSkuWillResaleAvailable = (skuId?: string): boolean => {
    const sku = data?.item?.skuSettings?.units.find(sku => sku.id === skuId)
    if (!sku) return false
    // チケットアイテムでないものはfalse
    if (data?.item?.type !== 'ticket') return false
    // リセール有効でないものはfalse
    if (!data?.item?.resaleEnabled) return false
    // リセール期間前ではないものはfalse

    if (new Date() > data?.item?.metadata?.ticket?.resalePeriod?.startedAt)
      return false
    // アイテム在庫があればfalse
    const stock = sku.stock ?? 0
    const purchaseQuantity =
      (sku.purchasedNumber ?? 0) + (sku.pendingPurchaseNumber ?? 0)

    if (stock > purchaseQuantity) return false

    return true
  }
  //skuの種類をクリックした時に、クリックしたskuのwillResaleAvailableがtrueかどうか判定
  useEffect(() => {
    const IsSkuWillResale = IsSkuWillResaleAvailable(skuId)
    setSkuWillResaleAvailable(IsSkuWillResale)
  }, [skuId, data, dataForGuest])

  //データを取得した時に、一つでもskuのwillResaleAvailableがtrueだった場合、ボタンを活性にするための処理
  useEffect(() => {
    const willResaleEnable = data?.item?.skuSettings?.units.some(sku =>
      IsSkuWillResaleAvailable(sku.id)
    )
    setSkuButtonActive(willResaleEnable)
  }, [data, dataForGuest])

  return {
    state: {
      currentUser: user,
      user: data?.user ?? null,
      artistId,
      loading: useInitialLoading(
        data ?? dataForGuest,
        loading || loadingForGuest || web3authWalletLoading
      ),
      hasItem,
      loadingItemWithSku,
      addingToCart: addingToCart || signingAnonymously,
      isLoggedIn: !!user,
      itemId: router.query.itemId,
      userLoggedInId: user?.uid ?? '',
      item,
      purchasablePlanNames,
      point: data?.prepaidPointBalance?.balance ?? 0,
      prepaidPointId: data?.prepaidPointBalance?.prepaidPointId ?? '',
      formattedPoint:
        data?.prepaidPointBalance?.formattedPrepaidPointBarance ?? '',
      userActiveResaleItemRaffle,
      isConnect: !!data?.user?.cryptoWallet?.address,
      paymentType: data?.item?.paymentType ?? '',
      purchasablePrepaidPoints: data?.purchasablePrepaidPoints ?? null,
      pointChargeLoading: chargeLoading,
      paymentSource: data?.paymentSource ?? null,
      isCardRegistered: !!data?.paymentSource?.last4,
      verifyingSMSCode,
      openSMSVerificationModal,
      sendingVerificationLoading,
      itemPurchasing,
      userCartCount: data?.userCartCount ?? 0,
      openExpiredCardModal,
      openPaymentErrorModal,
      openRequire3DSecureModal,
      skuButtonActive,
      skuWillResaleAvailable,
      /**  アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
      enablePointCarrierPayment:
        !!data?.organization?.modules?.pointCarrierPayment,
    },
    actions: {
      async onPurchaseItem(count, price, skuId) {
        let purchasePromise: Promise<unknown>
        // 無料アイテムの場合、purchaseItemByPointミューテーションで購入処理を行う
        if (!price) {
          purchasePromise = purchaseItemByPoint({
            variables: {
              itemId: router.query.itemId,
              prepaidPoint: 0,
              purchaseNumber: count,
              skuId: skuId ?? null,
            },
          })
        } else {
          purchasePromise = purchaseItemImmediately({
            variables: {
              inputs: {
                itemId: router.query.itemId,
                count,
                price,
                skuId: skuId ?? null,
              },
            },
            context: {
              headers: { 'x-utoniq-idempotency-key': createUuid() },
            },
          })
        }
        await purchasePromise
          .then(() => {
            analytics?.ecommerce({
              transaction_id: generateTransactionId(user?.uid),
              currency: 'JPY',
              type: 'convenience_store',
              items: [
                {
                  item_id: data?.item?.id ?? '',
                  item_name: data?.item?.title ?? '',
                  item_category: ConversionCategories.storeItem,
                  item_category2: data?.item?.type,
                  item_category3: platform,
                  item_category4: 'artistStoreItemDetail',
                  item_category5: artistId,
                  price: price,
                  quantity: count,
                },
              ],
            })
          })
          .catch(error => {
            analytics?.track('error_cart_checkout', {
              applicationKey: config.applicationKey,
              artistId,
              error: error?.name,
              errorCode: error?.graphQLErrors?.[0]?.extensions?.errorCode,
            })

            switch (error.graphQLErrors?.[0]?.extensions?.errorCode) {
              case constants.errorCodes.NEED_PATRON: {
                const e = new Error(t('needPatronErrorMessage'))
                showError(e)
                throw e
              }
              /**
               * @description
               * 3Dセキュア認証が必要な場合
               */
              case constants.errorCodes.REQUIRE_3D_SECURE:
                setOpenRequire3DSecureModal(true)
                throw error
              /**
               * @desciption
               * カードが有効期限切れの場合
               */
              case constants.errorCodes.EXPIRED_CARD:
                setOpenExpiredCardModal(true)
                throw error
              /**
               * @desciption
               * 何かしらの決済エラーの場合
               */
              case constants.errorCodes.PAYMENT_ERROR:
                setOpenPaymentErrorModal(true)
                throw error
              default:
                showError(error)
                throw error
            }
          })
      },
      connectMetamask: async () => {
        await userAuthorizeWalletBySignatureMessage()
        await refetch()

        return
      },
      connectToWeb3auth: async () => {
        /**
         * web3auth <=> firebase auth連携用のidToken取得
         */
        const idToken = await getAccessToken({ forceUpdateToken: true })
        if (!idToken) throw new Error('get jwtToke failed')

        /**
         * web3auth <=> firebase auth連携
         */
        await import('@/core/web3/web3auth').then(m =>
          m.connectToWeb3Auth(idToken)
        )

        return
      },
      // ポイントをチャージ
      onPurchaseCharge:
        platform === 'web' ? purchaseByWeb : purchaseByIosAndAndroid,
      async onUserInfoSetting(values) {
        // データをフォームに合わせるため、__typenameとfullAddressをaddressから除外する
        const { fullAddress, __typename, ...addressInput } = (data?.user
          ?.address || {
          postalCode: '',
          prefecture: '',
          city: '',
          line1: '',
          line2: '',
        }) as UserDeliveryInfoFragment['address'] & {
          __typename: string
        }
        return await updateUserDeliveryInfo({
          variables: {
            updateUserDeliveryInfoInputs: {
              fullName: values.fullName,
              phoneNumber: values.phoneNumber,
              address: addressInput,
            },
          },
        }).then(({ data }) => {
          if (!data?.updateUserDeliveryInfo.isPhoneNumberVerified) {
            setOpenSMSVerificationModal(true)
          }
          return !!data?.updateUserDeliveryInfo.isPhoneNumberVerified
        })
      },
      onCloseRequire3DSecureModal() {
        setOpenRequire3DSecureModal(false)
      },
      onCloseExpiredCardModal() {
        setOpenExpiredCardModal(false)
      },
      onClosePaymentErrorModal() {
        setOpenPaymentErrorModal(false)
      },
      onCloseSMSVerificationModal() {
        setOpenSMSVerificationModal(false)
      },
      async onSendVerificationSMS() {
        await sendVerificationSMS().then(() => {
          setOpenSMSVerificationModal(false)
        })
      },
      /**
       * @description
       * 一連の処理の流れは以下の通り
       * 1. カード登録に必要なトークンの発行
       * 2. 登録リクエストを投げると、正常終了 or 3DSecure 認証要求
       * 3. （3DSecure 認証が求められた場合のみ）
       *    3DSecure モーダルで認証を行う
       */
      async onCreatePayment(values) {
        const [expMonth, expYear] = values.expirationDate.split('/')
        /**
         * @description
         * クレジットカード登録に必要となるトークンを
         * 決済サービスプロバイダから発行する
         */
        const token = await createTokenWithCard({
          number: values.cardNumber.replace(/\s/g, ''),
          name: values.cardHolder,
          cvc: values.securityCode.replace(/\s/g, ''),
          expMonth,
          expYear,
        }).catch(error => {
          throw new Error(t(error.message))
        })
        /**
         * @description
         * トークンを元にクレジットカード登録を
         * 決済プロバイダーにリクエストする
         * 3DSecure が求められなかった場合 CreditCard 型を、
         * 3DSecure が求められた場合 RequiresAction 型を
         * value に持つデータが取得される
         */
        const { data: paymentResponse } = await createPaymentMethod({
          variables: { createPaymentMethodToken: token },
        })
        /**
         * @description
         * 3DSecure が求められた場合
         * すなわち RequiresAction 型のデータが取得された場合の処理
         */
        if (
          paymentResponse &&
          'clientSecret' in paymentResponse.createPaymentMethod
        ) {
          const { clientSecret } = paymentResponse.createPaymentMethod
          /**
           * @description
           * ここでモーダルが起動される
           */
          const result = await confirmCardSetupWithStripe(clientSecret)
          if (result.error) throw new Error(result.error.message)
        }
        /**
         * @description
         * webhook通知〜クレジットカード登録の完了を待つため
         * 暫定的に sleep を加えている
         */
        await sleep(2000)
        await refetch()
      },
      async onResendVerificationSMS() {
        await sendVerificationSMS()
      },
      async onVerificationPhoneNumber({ code }) {
        await verifySMSCode({ variables: { code } })
      },
      async onDeliveryInfoSetting(values) {
        const user = data?.user
        if (!user) return
        await updateUserDeliveryInfo({
          variables: {
            updateUserDeliveryInfoInputs: {
              fullName: user.fullName ?? '',
              phoneNumber: user.phoneNumber ?? '',
              address: {
                postalCode: values.postalCode,
                prefecture: values.prefecture,
                city: values.city,
                line1: values.line1,
                line2: values.line2,
              },
            },
          },
        })
      },
      // カートに入れる
      onAddToCart: async (inputs: AddItemToCartMutationVariables['inputs']) => {
        // 未ログインの場合、匿名ログイン処理を行う
        if (!user) {
          setSigningAnonymously(true)
          await signInAnonymously()
            .then(user => {
              if (!user) return
              setAnonymousUser({
                uid: user?.uid,
                displayName: user?.displayName,
              })
            })
            .finally(() => setSigningAnonymously(false))
        }

        return addToCart({
          variables: {
            inputs,
          },
        })
          .then(({ data: _data }) => {
            // イベントコレクションに関連づくアイテムの場合、イベントコレクション詳細に遷移する。それ以外のアイテムの場合、ストアアイテム一覧に遷移する
            const eventId = (data ?? dataForGuest)?.item?.collection?.event?.id
            const collectionId = (data ?? dataForGuest)?.item?.collection?.id

            if (collectionId && eventId) {
              router.replace(
                generatePath(routerPages.ShokufesEventCollectionDetail, {
                  eventId,
                  collectionId,
                })
              )
            } else {
              router.replace(
                generatePath(routerPages.ArtistStore, {
                  artistId,
                })
              )
            }

            analytics?.track('add_to_cart', {
              item_list_id: `artist_store_list_${artistId}`,
              item_list_name: 'Artist Store Items',
              isAnonymous: !!user?.isAnonymous,
              items: [
                {
                  item_id: inputs.itemId,
                  item_name: data?.item?.title,
                  item_category: 'store_item',
                  item_category2: data?.item?.type,
                  item_category3: App.getPlatform(),
                  item_category4: inputs.skuId,
                  item_category5: artistId,
                  price: inputs.count * (data?.item?.price ?? 0),
                  quantity: inputs.count,
                },
              ],
            })

            showMessage(
              t('addedToCart'),
              'success',
              undefined,
              <Typography
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  analytics?.track('click_cart', {
                    applicationKey: config.applicationKey,
                    type: 'snackBar',
                    isAnonymous: user?.isAnonymous,
                  })

                  router.push(
                    generatePath(routerPages.StoreCartDetail, {
                      cartId: _data?.addToCart.id,
                    })
                  )
                  handleClose()
                }}
                variant="subtitle1"
                color="white"
              >
                {t('showCart')}
              </Typography>,
              false
            )
          })
          .catch(e => {
            analytics?.track('error_cart_checkout', {
              applicationKey: config.applicationKey,
              artistId,
              error: e.name,
              errorCode: e.graphQLErrors[0].extensions?.errorCode ?? '',
            })

            if (
              e?.graphQLErrors?.[0]?.extensions?.errorCode === 'NEED_PATRON'
            ) {
              showError(new Error(t('needPatronErrorMessage')))
              return
            }

            showError(e)
          })
      },
      async onClickShare() {
        analytics?.track('click_share', {
          applicationKey: config.applicationKey,
          artistId: router,
          itemId: router.query.itemId,
          name: 'artistStoreItemDetail',
        })
        const url = await getShortLinkUrl()
        await Share.share({
          title: data?.item?.title,
          text: data?.item?.description || '',
          url: url,
          dialogTitle: data?.item?.title,
        })
          .then(() => {
            analytics?.track('click_shareMethod', {
              applicationKey: config.applicationKey,
              artistId:
                config.applicationType === 'standalone'
                  ? config.artistId
                  : router.query.artistId,
              itemId: router.query.itemId,
              name: 'artistStoreItemDetail',
            })
          })
          .catch(e => showError(e))

        return
      },
      async onGetArtistStoreItemDetailWithSku(skuId) {
        if (!user?.isSigned) return
        setSkuId(skuId)
        const { error } = await getArtistStoreItemDetailWithSku({
          variables: {
            skuId,
            id: router.query.itemId,
            itemId: router.query.itemId,
          },
        })
        if (error) {
          showError(error)
          throw error
        }
      },
    },
  }
})

const QUERY = gql`
  query ArtistStoreItemDetail(
    $id: ID!
    $artistId: ID!
    $itemId: String!
    $platform: String!
    $purchasablePrepaidPointPageInput: PageInput
    $skuId: String
  ) {
    artist(id: $artistId) {
      ...ArtistSubscriptionPlan
    }
    user {
      id
      phoneNumber
      fullName
      isPhoneNumberVerified
      formattedPhoneNumber @client
      ...UserDeliveryInfo
      cryptoWallet {
        address
      }
    }
    userCartCount
    item(id: $id) {
      ...StoreItemDetailGuest
    }
    userActiveResaleItemRaffle(itemId: $itemId) {
      id
      itemId
      skuId
    }
    prepaidPointBalance {
      balance
      prepaidPointId
      formattedPrepaidPointBarance @client
    }
    purchasablePrepaidPoints(
      platform: $platform
      pageInput: $purchasablePrepaidPointPageInput
    ) {
      ...PurchasablePrepaidPointConnection
    }
    paymentSource {
      ...paymentCreateDetail
    }
    organization {
      id
      modules {
        pointCarrierPayment
      }
    }
  }
  ${ArtistSubscriptionPlanFragmentDoc}
  ${UserDeliveryInfoFragmentDoc}
  ${StoreItemDetailGuestFragmentDoc}
  ${PurchasablePrepaidPointConnectionFragmentDoc}
  ${PaymentCreateDetailFragmentDoc}
`

const QUERY_ITEM_WITH_SKU = gql`
  query ArtistStoreItemDetailWithSku(
    $id: ID!
    $itemId: String!
    $skuId: String
  ) {
    item(id: $id) {
      ...StoreItemDetailGuestSku
    }
    userActiveResaleItemRaffle(itemId: $itemId, skuId: $skuId) {
      id
      itemId
    }
  }
  ${StoreItemDetailGuestSkuFragmentDoc}
`
const QUERY_FOR_GUEST = gql`
  query ArtistStoreItemDetailForGuest(
    $id: ID!
    $skuId: String
    $artistId: ID!
  ) {
    artist(id: $artistId) {
      ...ArtistSubscriptionPlan
    }
    item(id: $id) {
      ...StoreItemDetailGuest
    }
  }
  ${ArtistSubscriptionPlanFragmentDoc}
  ${StoreItemDetailGuestFragmentDoc}
`

const ADD_ITEM_TO_CART = gql`
  mutation AddItemToCart($inputs: AddToCartInput!) {
    addToCart(inputs: $inputs) {
      id
      totalCount
    }
  }
`

const PURCHASE_ITEM_BY_POINT = gql`
  mutation PurchaseItemByPoint(
    $itemId: ID!
    $purchaseNumber: Int
    $prepaidPoint: Int
    $skuId: String
  ) {
    purchaseItemByPoint(
      itemId: $itemId
      purchaseNumber: $purchaseNumber
      prepaidPoint: $prepaidPoint
      skuId: $skuId
    ) {
      id
      price
      userId
      item {
        ...StoreItemDetail
      }
    }
  }
  ${StoreItemDetailFragmentDoc}
`

const VIEW_STORE_ITEM = gql`
  mutation ViewStoreItem($inputs: ViewStoreItemInput!) {
    viewStoreItem(inputs: $inputs) {
      id
      userId
      itemId
      viewCount
    }
  }
`

const PURCHASE_ITEM_IMMEDIATELY = gql`
  mutation PurchaseItemImmediately(
    $inputs: PurchaseItemImmediatelyInput!
    $skuId: String
  ) {
    purchaseItemImmediately(inputs: $inputs) {
      id
      paymentType
      items {
        item {
          ...StoreItemDetail
        }
      }
    }
  }
  ${StoreItemDetailFragmentDoc}
`
